import React, { createContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { GetSmartSpotPerformanceChartResponse } from '@classpass/protobuf-definitions/lib/web/models/partners/smartspot_opportunities';

import URLS from 'constants/urls';
import { httpClient } from 'helpers/http';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { selectActiveVenueId, selectVenueSmartspotData } from 'selectors/general';

const DEFAULT_VALUE = {
  chartData: [],
  isLoading: false,
  error: null,
};

const SmartToolsContext = createContext(DEFAULT_VALUE);

async function fetchSmartSpotFillChartData(venueId) {
  try {
    const endpoint = URLS.venue.smartSpotFillChartData(venueId);
    const response = await httpClient.get(endpoint);
    const deserialized = GetSmartSpotPerformanceChartResponse.fromJSON(response.data);
    const transformedChartData = transformKeysToCamelCase(deserialized.chartData || []);
    return transformedChartData;
  } catch (error) {
    reportError(error);
    return [];
  }
}

export function SmartToolsContextProvider({ children }) {
  const venueId = useSelector(selectActiveVenueId);
  const venueSmartspotData = useSelector(selectVenueSmartspotData);
  const isEnabled = venueSmartspotData?.isEnabled ?? false;
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!venueId) {
      setChartData([]);
      return;
    }

    if (!isEnabled) {
      setChartData([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    async function fetchData() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchSmartSpotFillChartData(venueId);
        setChartData(data);
      } catch (err) {
        setError(err);
        setChartData([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [venueId, isEnabled]);

  const value = {
    chartData,
    isLoading,
    error,
  };

  return <SmartToolsContext.Provider value={value}>{children}</SmartToolsContext.Provider>;
}

SmartToolsContextProvider.propTypes = {
  children: PropTypes.node,
};

export default SmartToolsContext;
